import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Admin.css";
import "./Students.css";
import { API_BASE_URL } from "../../config";

const Students = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    // Filter / Sort / Search state
    const [search, setSearch] = useState("");
    const [filterSection, setFilterSection] = useState("All");
    const [sortBy, setSortBy] = useState("newest");

    // Form fields
    const [form, setForm] = useState({
        fullName: "",
        email: "",
        whatsapp: "",
        class: "",
        section: "",
        rollNumber: "",
        admissionNumber: "",
    });

    useEffect(() => {
        const token = localStorage.getItem("admin_token");
        if (!token) {
            navigate("/admin/anubhava-montessori/qwerzxcv6909/login");
            return;
        }
        fetchStudents(token);
    }, [navigate]);

    const fetchStudents = async (token) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/students`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 401 || res.status === 403) {
                localStorage.removeItem("admin_token");
                navigate("/admin/anubhava-montessori/qwerzxcv6909/login");
                return;
            }
            const data = await res.json();
            setStudents(data);
        } catch (err) {
            setError("Failed to fetch students.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("admin_token");
        navigate("/admin/anubhava-montessori/qwerzxcv6909/login");
    };

    // Derive unique sections from students list
    const availableSections = useMemo(() => {
        const sectionSet = new Set(students.map((s) => `${s.class} - ${s.section}`));
        return ["All", ...Array.from(sectionSet).sort()];
    }, [students]);

    // Filtered + sorted + searched students
    const displayedStudents = useMemo(() => {
        let list = [...students];

        // Filter by section
        if (filterSection !== "All") {
            const [cls, sec] = filterSection.split(" - ");
            list = list.filter((s) => s.class === cls && s.section === sec);
        }

        // Search
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            list = list.filter(
                (s) =>
                    s.fullName.toLowerCase().includes(q) ||
                    (s.email && s.email.toLowerCase().includes(q)) ||
                    s.whatsapp.includes(q) ||
                    (s.admissionNumber && s.admissionNumber.toLowerCase().includes(q)) ||
                    (s.rollNumber && s.rollNumber.toLowerCase().includes(q))
            );
        }

        // Sort
        switch (sortBy) {
            case "a-z":
                list.sort((a, b) => a.fullName.localeCompare(b.fullName));
                break;
            case "z-a":
                list.sort((a, b) => b.fullName.localeCompare(a.fullName));
                break;
            case "oldest":
                list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                break;
            case "newest":
            default:
                list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                break;
        }

        return list;
    }, [students, filterSection, search, sortBy]);

    const openCreateModal = () => {
        setEditingStudent(null);
        setForm({ fullName: "", email: "", whatsapp: "", class: "", section: "", rollNumber: "", admissionNumber: "" });
        setShowModal(true);
    };

    const openEditModal = (student) => {
        setEditingStudent(student);
        setForm({
            fullName: student.fullName,
            email: student.email || "",
            whatsapp: student.whatsapp,
            class: student.class,
            section: student.section,
            rollNumber: student.rollNumber || "",
            admissionNumber: student.admissionNumber || "",
        });
        setShowModal(true);
    };

    const handleFormChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.fullName || !form.whatsapp || !form.class || !form.section) {
            alert("Full Name, WhatsApp, Class, and Section are required.");
            return;
        }
        setSaveLoading(true);
        const token = localStorage.getItem("admin_token");
        const url = editingStudent
            ? `${API_BASE_URL}/api/students/${editingStudent._id}`
            : `${API_BASE_URL}/api/students`;
        const method = editingStudent ? "PUT" : "POST";

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ ...form }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Failed to save.");
            setShowModal(false);
            fetchStudents(token);
        } catch (err) {
            alert(err.message);
        } finally {
            setSaveLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem("admin_token");
        try {
            const res = await fetch(`${API_BASE_URL}/api/students/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                setStudents((prev) => prev.filter((s) => s._id !== id));
                setDeleteConfirmId(null);
            } else {
                alert("Failed to delete student.");
            }
        } catch {
            alert("Error connecting to server.");
        }
    };

    const openWhatsApp = (number) => {
        const cleaned = number.replace(/\D/g, "");
        const withCountry = cleaned.startsWith("91") ? cleaned : `91${cleaned}`;
        window.open(`https://wa.me/${withCountry}`, "_blank");
    };

    const formatDate = (d) =>
        new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <div className="dashboard-sidebar">
                <div className="sidebar-logo">Anubhava</div>
                <ul className="sidebar-menu">
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/blogs")}>
                        <i className="fa-solid fa-blog"></i>
                        <span>Manage Blogs</span>
                    </li>
                    <li className="sidebar-item active" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/students")}>
                        <i className="fa-solid fa-users"></i>
                        <span>Students CRM</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/gallery")}>
                        <i className="fa-solid fa-images"></i>
                        <span>Gallery</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/gallery")}>
                        <i className="fa-solid fa-globe"></i>
                        <span>Visit Website</span>
                    </li>
                </ul>
                <div className="sidebar-logout">
                    <button onClick={handleLogout} className="btn-secondary" style={{ width: "100%" }}>Log Out</button>
                </div>
            </div>

            {/* Main Content */}
            <div className="dashboard-content">
                {/* Header */}
                <div className="dashboard-header">
                    <div>
                        <h1>Students CRM</h1>
                        <p style={{ color: "#777", fontSize: "15px", marginTop: "4px" }}>
                            {students.length} total student{students.length !== 1 ? "s" : ""} enrolled
                        </p>
                    </div>
                    <button className="new-blog-btn" onClick={openCreateModal}>
                        <i className="fa-solid fa-user-plus"></i> Add New Student
                    </button>
                </div>

                {/* Search + Filter + Sort Toolbar */}
                <div className="students-toolbar">
                    <div className="search-bar-wrapper">
                        <i className="fa-solid fa-magnifying-glass search-icon"></i>
                        <input
                            type="text"
                            placeholder="Search by name, email, phone, roll no, admission no..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="students-search-input"
                        />
                        {search && (
                            <button className="clear-search-btn" onClick={() => setSearch("")}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        )}
                    </div>

                    <div className="toolbar-filters">
                        <div className="filter-group">
                            <label><i className="fa-solid fa-filter"></i> Class/Section</label>
                            <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)}>
                                {availableSections.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label><i className="fa-solid fa-arrow-up-wide-short"></i> Sort By</label>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="a-z">Name A → Z</option>
                                <option value="z-a">Name Z → A</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Summary */}
                {search && (
                    <p className="search-results-info">
                        Showing <strong>{displayedStudents.length}</strong> result{displayedStudents.length !== 1 ? "s" : ""} for "<strong>{search}</strong>"
                    </p>
                )}

                {/* Student List */}
                {loading ? (
                    <div className="students-loading">Loading student records...</div>
                ) : error ? (
                    <div className="login-error-msg">{error}</div>
                ) : displayedStudents.length === 0 ? (
                    <div className="students-empty">
                        <i className="fa-solid fa-user-slash" style={{ fontSize: "48px", color: "#ddd", marginBottom: "15px" }}></i>
                        <h3>{search || filterSection !== "All" ? "No students match your filters." : "No students added yet."}</h3>
                        {!search && filterSection === "All" && (
                            <p>Click <strong>Add New Student</strong> to get started.</p>
                        )}
                    </div>
                ) : (
                    <div className="students-list-container">
                        {displayedStudents.map((student) => (
                            <div key={student._id} className="student-card">
                                <div className="student-card-left">
                                    <div className="student-avatar">
                                        {student.fullName.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="student-info">
                                        <h3 className="student-name">{student.fullName}</h3>
                                        <div className="student-meta-row">
                                            <span className="student-class-badge">
                                                {student.class} — {student.section}
                                            </span>
                                            {student.rollNumber && (
                                                <span className="student-detail-tag">Roll: {student.rollNumber}</span>
                                            )}
                                            {student.admissionNumber && (
                                                <span className="student-detail-tag">Adm: {student.admissionNumber}</span>
                                            )}
                                        </div>
                                        <div className="student-contact-row">
                                            {student.email && (
                                                <span className="student-contact-item">
                                                    <i className="fa-solid fa-envelope"></i> {student.email}
                                                </span>
                                            )}
                                            <span className="student-contact-item">
                                                <i className="fa-brands fa-whatsapp"></i> {student.whatsapp}
                                            </span>
                                            <span className="student-contact-item muted">
                                                <i className="fa-regular fa-calendar"></i> Added {formatDate(student.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="student-card-actions">
                                    <button
                                        className="whatsapp-cta-btn"
                                        onClick={() => openWhatsApp(student.whatsapp)}
                                        title="Open WhatsApp chat"
                                    >
                                        <i className="fa-brands fa-whatsapp"></i>
                                        <span>WhatsApp</span>
                                    </button>
                                    <button className="action-icon-btn edit" title="Edit student" onClick={() => openEditModal(student)}>
                                        <i className="fa-solid fa-pencil"></i>
                                    </button>
                                    {deleteConfirmId === student._id ? (
                                        <div className="delete-confirm-inline">
                                            <span>Delete?</span>
                                            <button className="confirm-yes-btn" onClick={() => handleDelete(student._id)}>Yes</button>
                                            <button className="confirm-no-btn" onClick={() => setDeleteConfirmId(null)}>No</button>
                                        </div>
                                    ) : (
                                        <button className="action-icon-btn delete" title="Delete student" onClick={() => setDeleteConfirmId(student._id)}>
                                            <i className="fa-solid fa-trash-can"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add/Edit Student Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingStudent ? "Edit Student" : "Add New Student"}</h2>
                            <button className="modal-close-btn" onClick={() => setShowModal(false)}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="modal-form">
                            <div className="modal-form-grid">
                                <div className="form-group">
                                    <label>Full Name <span className="required-star">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Aarav Sharma"
                                        value={form.fullName}
                                        onChange={(e) => handleFormChange("fullName", e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email Address</label>
                                    <input
                                        type="email"
                                        placeholder="parent@email.com"
                                        value={form.email}
                                        onChange={(e) => handleFormChange("email", e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>WhatsApp Number <span className="required-star">*</span></label>
                                    <input
                                        type="tel"
                                        placeholder="e.g. 9246182801"
                                        value={form.whatsapp}
                                        onChange={(e) => handleFormChange("whatsapp", e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Class <span className="required-star">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Primary, Toddler, Grade 1"
                                        value={form.class}
                                        onChange={(e) => handleFormChange("class", e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Section <span className="required-star">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Sunflower, A, B"
                                        value={form.section}
                                        onChange={(e) => handleFormChange("section", e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Roll Number <span className="optional-tag">(optional)</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. 14"
                                        value={form.rollNumber}
                                        onChange={(e) => handleFormChange("rollNumber", e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Admission Number <span className="optional-tag">(optional)</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. AM2024-001"
                                        value={form.admissionNumber}
                                        onChange={(e) => handleFormChange("admissionNumber", e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary" disabled={saveLoading}>
                                    {saveLoading ? "Saving..." : editingStudent ? "Update Student" : "Add Student"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Students;
